<template>
  <div>
    <h2>
      Registrer eksisterende leieforhold for <strong>{{ defaultHeader }}</strong>
    </h2>

    <InfoCard class="mt-5">
      <div>
        <p>
          Her kan du registere leieforhold som allerede er opprettet og etablert. Velg først om du vil
          Husleie.no skal forvalte leieforholdet med et Basic eller Premium abonnement, eller om du
          kun vil lagre dataene om leieforholdet slik at du kan benytte andre tjenester.
        </p>

        <p>
          OBS! Hvis du ønsker å opprette en ny leiekontrakt for et nytt leieforhold, gå til Min Side
          igjen og velg “Opprett leiekontrakt“.
        </p>
      </div>
    </InfoCard>

    <BlockContainer class="blockContainer mt-4">
      <ValidationObserver ref="newTenancyForm">
        <form @submit.prevent="onSubmit">
          <!-- Plans-->
          <div class="pt-4">
            <h5>Velg om du vil forvalte leieforholdet</h5>

            <p>
              Ved å bestille et abonnement godkjenner du
              <a
                href="https://www.husleie.no/juridisk/vilkar-for-basic-og-premium/"
                target="_blank"
                rel="noopener"
              >
                abonnementsvilkårene.
              </a>

              Se detaljer om våre abonnementer ved å klikke
              <a href="https://www.husleie.no/utleier/priser/" target="_blank" rel="noopener">her.</a>
            </p>

            <ValidationProvider rules="required" v-slot="{ errors }">
              <input type="hidden" :value="tenancy.chosenPlan" />

              <div class="newTenancyFormGrid text-center mt-5" :class="{'is-invalid': errors[0] }">
                <hl-RadioButtonTapWithIcon
                  v-for="sub of availableSubscriptions"
                  :key="sub.id"
                  :iconClass="
                    sub.name in subscriptionPlanInfo ? subscriptionPlanInfo[sub.name].icon : 'budicon-toaster'
                  "
                  layout="vertical"
                  :radioValue="sub.id"
                  :value="String(tenancy.chosenPlan)"
                  @input="planId => tenancy.chosenPlan = Number(planId)"
                >
                  <p class="font-weight-bold">
                    {{ sub.name in subscriptionPlanInfo ? subscriptionPlanInfo[sub.name].text : 'Ukjent plan' }}
                  </p>
                  <p v-if="sub.price > 0">{{ sub.price }} kr / mnd{{ 'special_price' in sub ? '*' : '' }}</p>
                  <p v-if="'special_price' in sub" class="small">* avtalt pris</p>
                </hl-RadioButtonTapWithIcon>
              </div>
              <hl-ErrorMessage v-if="errors[0]" class="mt-2">
                Du må velge et forvaltningsabonnement.
              </hl-ErrorMessage>
            </ValidationProvider>
          </div>

          <p v-if="tenancy.chosenPlan === 0" class="mt-3 text-warning">
            Ved valg av «Ingen forvaltning» vil Husleie.no ikke sende faktura eller kreve inn husleien.
          </p>

          <div class="mt-5">
            <h5>Informasjon om leietaker</h5>

            <div class="row">
              <div class="col-12 col-md-6">
                <hl-SwitchWithLabels
                  class="mt-4"
                  v-model="tenantIsCompany"
                  showState
                  onState="Virksomhet"
                  offState="Privatperson"
                ></hl-SwitchWithLabels>
              </div>
            </div>

            <div v-if="tenantIsCompany" class="row mt-5">
              <div class="col">
                <hl-SearchCompanies
                  :companyName="tenancy.tenant.company.organizationName"
                  @companySet="populateCompany"
                ></hl-SearchCompanies>
              </div>

              <div class="col">
                <hl-Input
                  disabled
                  label="Organisasjonsnavn"
                  v-model="tenancy.tenant.company.organizationNumber"
                ></hl-Input>
              </div>
            </div>

            <div class="newTenancyFormGrid mt-5">
              <div>
                <ValidationProvider
                  name="Leietakers fulle navn"
                  :rules="validationRules.tenancy.newTenancyForm.tenant.fullName"
                  v-slot="{ errors }"
                >
                  <hl-Input
                    type="text"
                    label="Leietakers fulle navn"
                    v-model="tenancy.tenant.fullName"
                    :error="errors[0]"
                  ></hl-Input>
                </ValidationProvider>
              </div>

              <ValidationProvider
                v-if="!tenantIsCompany"
                :rules="validationRules.tenancy.newTenancyForm.tenant.dateOfBirth"
                v-slot="{ errors }"
              >
                <hl-Input
                  type="text"
                  label="Fødselsdato"
                  placeholder="DD.MM.ÅÅÅÅ"
                  v-model="tenancy.tenant.dateOfBirth"
                  v-mask="'##.##.####'"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>

            <div class="newTenancyFormGrid mt-4">
              <div>
                <hl-Phone type="text" label="Landkode" placeholder="+47" v-model="tenancy.tenant.phonePrefix" disabled>
                  <ValidationProvider
                    name="Telefon"
                    :rules="validationRules.tenancy.newTenancyForm.tenant.cellPhone"
                    :debounce="1000"
                    v-slot="{ errors }"
                  >
                    <hl-Input type="tel" label="Telefon" v-model="tenancy.tenant.cellPhone" :error="errors[0]" />
                  </ValidationProvider>
                </hl-Phone>
              </div>

              <div>
                <ValidationProvider
                  name="E-post"
                  :rules="validationRules.tenancy.newTenancyForm.tenant.email"
                  :debounce="1000"
                  v-slot="{ errors }"
                >
                  <hl-Input type="text" label="E-post" v-model="tenancy.tenant.email" :error="errors[0]"></hl-Input>
                </ValidationProvider>
              </div>
            </div>

            <div v-if="isLandlordCompany" class="mt-5">
              <h6 class="text-secondary">Valgfri info for fakturering og andre formål</h6>
              <div class="mt-3">
                <hl-Input type="text" label="Adresse" v-model="tenancy.tenant.address.street"></hl-Input>

                <div class="newTenancyFormGrid mt-4">
                  <div>
                    <hl-Input
                      type="text"
                      label="Postnummer"
                      v-model="tenancy.tenant.address.postalZipcode"
                      @input="fetchPostalArea"
                    ></hl-Input>
                  </div>

                  <div>
                    <hl-Input
                      type="text"
                      label="Poststed"
                      v-model="tenancy.tenant.address.postalArea"
                      disabled
                    ></hl-Input>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="my-5 pt-4">
            <h5>Medleietaker(e)</h5>

            <List class="my-5">
              <ListHeader v-if="tenancy.coTenants.length">
                <div class="col">Navn</div>
                <div class="col coTenant-phone">Telefon</div>
                <div class="col">E-post</div>
                <div class="col delete-list-item">&nbsp;</div>
              </ListHeader>
              <!-- Looping coTenants -->
              <ListItem v-for="(coTenant, i) in tenancy.coTenants" :key="i">
                <div class="col">
                  <ValidationProvider
                    name="Medleietakersnavn"
                    :rules="validationRules.tenancy.newTenancyForm.coTenant.fullName"
                    v-slot="{ errors }"
                  >
                    <hl-Input type="text" size="sm" v-model="coTenant.fullName" :error="errors[0]"></hl-Input>
                  </ValidationProvider>
                </div>

                <div class="col coTenant-phone">
                  <ValidationProvider
                    name="Telefon"
                    :rules="validationRules.tenancy.newTenancyForm.coTenant.cellPhone"
                    v-slot="{ errors }"
                    :debounce="1000"
                  >
                    <hl-Input type="text" size="sm" v-model="coTenant.cellPhone" :error="errors[0]"></hl-Input>
                  </ValidationProvider>
                </div>

                <div class="col">
                  <ValidationProvider
                    name="E-post"
                    :rules="validationRules.tenancy.newTenancyForm.coTenant.email"
                    v-slot="{ errors }"
                    :debounce="1000"
                  >
                    <hl-Input type="text" size="sm" v-model="coTenant.email" :error="errors[0]"></hl-Input>
                  </ValidationProvider>
                </div>

                <div class="col delete-list-item">
                  <hl-Button variant="white" class="py-0" @click="removeCoTenantItem(i)">
                    <i class="budicon-trash"></i>
                  </hl-Button>
                </div>
              </ListItem>

              <ListItem class="add-row mb-4" @click.native="addCoTenantItem">
                <div class="col text-center">Legg til medleietaker</div>
              </ListItem>
            </List>
          </div>

          <div class="my-5 pt-4">
            <h5>Informasjon om leieforholdet</h5>

            <!-- Choose contract duration -->
            <div class="newTenancyFormGrid text-center mt-5">
              <div>
                <hl-RadioButtonTapWithIcon iconClass="budicon-hourglass" v-model="durationType" radioValue="limited">
                  <p>Leieforholdet er</p>
                  <p class="font-weight-bold">tidsbestemt</p>
                </hl-RadioButtonTapWithIcon>
              </div>

              <div>
                <hl-RadioButtonTapWithIcon
                  iconClass="budicon-forward-time"
                  v-model="durationType"
                  radioValue="unlimited"
                >
                  <p>Leieforholdet er</p>
                  <p class="font-weight-bold">tidsubestemt</p>
                </hl-RadioButtonTapWithIcon>
              </div>
            </div>
            <!-- / Choose contract duration -->

            <div class="newTenancyFormGrid mt-5">
              <div>
                <ValidationProvider
                  name="Startdato"
                  :rules="validationRules.tenancy.newTenancyForm.contractDetails.startDate"
                  v-slot="{ errors }"
                >
                  <hl-Datepicker
                    label="Startdato"
                    v-model="tenancy.contractDetails.startDate"
                    name="rent.startDate"
                    :error="errors[0]"
                  ></hl-Datepicker>
                </ValidationProvider>
              </div>

              <div>
                <ValidationProvider
                  v-if="durationType === 'limited'"
                  name="Sluttdato"
                  :rules="validationRules.tenancy.newTenancyForm.contractDetails.endDate"
                  v-slot="{ errors }"
                >
                  <hl-Datepicker
                    label="Sluttdato"
                    v-model="tenancy.contractDetails.endDate"
                    name="rent.endDate"
                    :error="errors[0]"
                  ></hl-Datepicker>
                </ValidationProvider>
              </div>
            </div>

            <div class="newTenancyFormGrid mt-4">
              <div>
                <ValidationProvider
                  name="Bindingstid"
                  :rules="validationRules.tenancy.newTenancyForm.contractDetails.bindingPeriod"
                  v-slot="{ errors }"
                >
                  <hl-Select
                    label="Bindingstid"
                    placeholder="Velg bindingstid"
                    placeholderValue=""
                    v-model="tenancy.contractDetails.bindingPeriod"
                    :options="bindingstidOptions"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </div>

              <div>
                <ValidationProvider
                  name="Oppsigelsestid"
                  :rules="validationRules.tenancy.newTenancyForm.contractDetails.noticePeriod"
                  v-slot="{ errors }"
                >
                  <hl-Select
                    label="Oppsigelsestid"
                    placeholder="Velg oppsigelsestid"
                    placeholderValue=""
                    v-model="tenancy.contractDetails.noticePeriod"
                    :options="oppsigelsestidOptions"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </div>
            </div>

            <div class="newTenancyFormGrid mt-4">
              <div>
                <ValidationProvider
                  name="Betalingsfrekvens"
                  :rules="validationRules.tenancy.newTenancyForm.contractDetails.invoicePeriod"
                  v-slot="{ errors }"
                >
                  <hl-Select
                    label="Betalingsfrekvens"
                    placeholder="Velg periode"
                    placeholderValue=""
                    v-model="tenancy.contractDetails.rent.invoicePeriod"
                    :options="invoicePeriodOptions"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </div>

              <div>
                <ValidationProvider
                  name="Forfallsdag"
                  :rules="validationRules.tenancy.newTenancyForm.contractDetails.dueDay"
                  v-slot="{ errors }"
                >
                  <hl-Select
                    label="Forfallsdag"
                    placeholder="Velg forfallsdag"
                    placeholderValue=""
                    v-model="tenancy.contractDetails.rent.dueDay"
                    :options="dueDayOptions"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </div>
            </div>

            <div v-if="tenancy.contractDetails.startDate && tenancy.contractDetails.rent.dueDay" class="mt-4">
              <ValidationProvider
                name="Husleieperiode"
                :rules="validationRules.tenancy.newTenancyForm.contractDetails.duePeriod"
                v-slot="{ errors }"
              >
                <hl-Select
                  :label="translationDuePeriodLabel"
                  :placeholder="translationDuePeriodPlaceholder"
                  placeholderValue=""
                  v-model="tenancy.contractDetails.rent.duePeriod"
                  :options="duePeriodOptions"
                  :disabled="tenancy.contractDetails.rent.invoicePeriod === 'quarter'"
                  :error="errors[0]"
                />
              </ValidationProvider>

              <strong v-if="tenancy.contractDetails.rent.duePeriod">
                {{ translationPeriodInfo }}
              </strong>
            </div>
          </div>

          <div class="mt-5 pt-4">
            <h5>Informasjon om husleien og eventuelle tilleggstjenester</h5>

            <List class="mt-5">
              <ListHeader>
                <div class="newTenancyFormGrid4 w-100">
                  <div>Produkt</div>
                  <div v-if="isLandlordCompany">MVA</div>
                  <div>Beløp per måned</div>
                  <div class="delete-list-item"></div>
                </div>
              </ListHeader>

              <ListItem>
                <div class="newTenancyFormGrid4">
                  <span>Husleie</span>

                  <hl-Select
                    v-if="isLandlordCompany"
                    size="sm"
                    :hasMarginBottom="false"
                    :options="MVAOptions"
                    v-model="tenancy.contractDetails.rent.vat"
                  />

                  <ValidationProvider
                    name="Husleie"
                    :rules="validationRules.tenancy.newTenancyForm.contractDetails.rent.amount"
                    v-slot="{ errors }"
                  >
                    <hl-Input
                      size="sm"
                      v-model="tenancy.contractDetails.rent.amount"
                      is-text-right-aligned
                      :error="errors[0]"
                    />
                  </ValidationProvider>

                  <div v-if="!isLandlordCompany" />

                  <div class="d-flex justify-content-end">
                    <hl-Button variant="white" size="sm" class="py-0" disabled>
                      <i class="budicon-block-sign"></i>
                    </hl-Button>
                  </div>
                </div>
              </ListItem>

              <ListItem v-for="(subProduct, j) in tenancy.subProducts" :key="j">
                <div class="newTenancyFormGrid4">
                  <ValidationProvider
                    name="Tilleggsproduktnavn"
                    :rules="validationRules.tenancy.newTenancyForm.contractDetails.rent.subproduct.name"
                    v-slot="{ errors }"
                  >
                    <hl-Input size="sm" v-model="subProduct.name" :error="errors[0]"></hl-Input>
                  </ValidationProvider>

                  <hl-Select
                    v-if="isLandlordCompany"
                    size="sm"
                    :hasMarginBottom="false"
                    :options="MVAOptions"
                    v-model="subProduct.vat"
                  />

                  <ValidationProvider
                    name="Tilleggsproduktpris"
                    :rules="validationRules.tenancy.newTenancyForm.contractDetails.rent.subproduct.price"
                    v-slot="{ errors }"
                  >
                    <hl-Input
                      size="sm"
                      v-model="subProduct.price"
                      :error="errors[0]"
                      is-text-right-aligned
                    />
                  </ValidationProvider>

                  <div v-if="!isLandlordCompany" />

                  <div class="d-flex justify-content-end">
                    <hl-Button size="sm" variant="white" @click="removeSubProductItem(j)">
                      <i class="budicon-trash"></i>
                    </hl-Button>
                  </div>
                </div>
              </ListItem>

              <ListItem class="add-row mb-4" @click.native="addSubProductItem">
                <div class="col text-center">Legg til produktlinje</div>
              </ListItem>

              <div v-if="isLandlordCompany" class="mt-4">
                <ListHeader>
                  <div class="col">
                    <h6 class="font-weight-normal">Total uten MVA</h6>
                  </div>
                  <div class="col percentage">&nbsp;</div>
                  <div class="col amount">
                    <h6 class="font-weight-normal">
                      <hl-Currency :amount="netTotal"></hl-Currency>
                    </h6>
                  </div>
                </ListHeader>

                <ListHeader>
                  <div class="col">
                    <h6 class="font-weight-normal">Total MVA</h6>
                  </div>
                  <div class="col percentage">&nbsp;</div>
                  <div class="col amount">
                    <h6 class="font-weight-normal">
                      <hl-Currency :amount="totalTax"></hl-Currency>
                    </h6>
                  </div>
                </ListHeader>
              </div>

              <hr />

              <ListHeader>
                <div class="col">
                  <h6>Totalt per måned</h6>
                </div>
                <div class="col percentage">&nbsp;</div>
                <div class="col amount">
                  <h6>
                    <hl-Currency :amount="totalWithTax"></hl-Currency>
                  </h6>
                </div>
              </ListHeader>
            </List>
          </div>

          <div class="my-5 pt-4">
            <h5>Bankkonto for betaling av husleien</h5>

            <div class="mt-5">
              <List>
                <BlockContainer class="blockContainer">
                  <div>
                    <hl-SwitchWithLabels
                      class="mt-4"
                      v-model="newBankAccountToggle"
                      showState
                      onState="Ny bankkonto"
                      offState="Eksisterende bankkonto"
                    />
                    <ValidationProvider
                      v-if="newBankAccountToggle"
                      name="Bankkonto"
                      :rules="validationRules.tenancy.newTenancyForm.bankAccount"
                      v-slot="{ errors }"
                    >
                      <hl-Input
                        class="my-4"
                        label="Bankkonto"
                        v-model="tenancy.bankAccount.accountNumber"
                        :error="errors[0]"
                      />
                    </ValidationProvider>
                    <ValidationProvider
                      v-else
                      name="Bankkonto"
                      :rules="validationRules.tenancy.newTenancyForm.bankAccount"
                      v-slot="{ errors }"
                    >
                      <hl-Select
                        class="my-4"
                        label="Velg bankkonto"
                        placeholder="Velg bankkonto"
                        v-model="tenancy.bankAccount.accountNumber"
                        :options="bankAccountOptions"
                        :error="errors[0]"
                      />
                    </ValidationProvider>
                    <hl-Input class="my-3" label="Alias" v-model="tenancy.bankAccount.alias" />
                  </div>
                </BlockContainer>
              </List>
            </div>
          </div>

          <div class="my-5 pt-4">
            <h5>Informasjon om depositum</h5>

            <p>
              Registrer informasjon om depositum for dette leieforholdet. Du kan også gjøre
              dette senere.
            </p>

            <hl-Select
              class="my-4"
              label="Velg eksisterende depositumtype"
              placeholder="Velg depositumtype"
              placeholderValue="none"
              v-model="depDeposit.category"
              :options="depAccountOptions"
            />

            <TenancyDepositRegisterForms
              v-if="depDeposit.category !== 'none'"
              :usedInCreateNewTenancy="true"
              :deposit="depDeposit"
              @updatedLocalCopy="setDepDeposit"
              @gotFiles="handleDepositFilesUpload"
            />
          </div>

          <div v-if="isPremiumSelected" class="my-5 pt-4">
            <h5>Indeksregulering av husleien</h5>

            <p>
              Siden du har valgt Premium forvaltning av leieforholdet vil du her kunne
              aktivere automatisk, årlig indeksregulering av husleien.
            </p>

            <div
              v-if="tenancy.contractDetails.rent.dueDay && tenancy.contractDetails.startDate"
              class="row mt-5 align-items-center"
            >
              <div class="col">
                <hl-Switch
                  class="my-5 d-inline-block"
                  showState
                  onState="På"
                  offState="Av"
                  v-model="tenancy.CPIAdjustment.CPIAdjustmentActive"
                ></hl-Switch>
              </div>

              <div class="col">
                <ValidationProvider
                  name="Neste indeksregulering"
                  :rules="validationRules.tenancy.newTenancyForm.CPIAdjustment.nextCPIAdjustment"
                  v-slot="{ errors }"
                  :disabled="!tenancy.CPIAdjustment.CPIAdjustmentActive"
                  ref="nextCPIAdj"
                >
                  <hl-Select
                    :disabled="!tenancy.CPIAdjustment.CPIAdjustmentActive"
                    placeholder="Ikke satt"
                    label="Neste indeksregulering"
                    :options="CPIAdjustmentDateOptions"
                    :error="errors[0]"
                    v-model="tenancy.CPIAdjustment.nextCPIAdjustment"
                  ></hl-Select>
                </ValidationProvider>
              </div>
            </div>

            <div v-else class="mt-5">
              <hl-Alert variant="info"
                >Du må registrere startdato og forfallsdag for å aktivere automatisk indeksregulering.</hl-Alert
              >
            </div>
          </div>

          <!-- Only if landlord is Pro -->
          <div v-if="isLandlordProfessional" class="my-5 pt-4">
            <h5>Kundeinformasjon</h5>

            <p>
              Dersom du har behov for å linke dette leieforholdet opp mot regnskapssystemer kan
              du legge inn dine egne kundenummer og/eller PO-nummer/Referansenummer.
            </p>

            <div class="row mt-4">
              <div class="col">
                <hl-Input
                  type="text"
                  label="Kundenummer (for regnskap el.l)"
                  v-model="tenancy.customerInfo.customerClientId"
                ></hl-Input>
              </div>

              <div class="col">
                <hl-Input
                  type="text"
                  label="PO-nummer / Referansenummer"
                  v-model="tenancy.customerInfo.poNumber"
                ></hl-Input>
              </div>
            </div>
          </div>

          <div class="buttons">
            <hl-Button variant="secondary" type="button" @click="cancelTenancy"> Avbryt </hl-Button>
            <hl-Button variant="primary" :disabled="loading" :loading="loading" type="submit">
              Lagre
            </hl-Button>
          </div>
        </form>
      </ValidationObserver>
    </BlockContainer>
  </div>
</template>

<script>
import TenancyDepositRegisterForms from '@/components/tenancy/deposit/TenancyDepositRegisterForms';
import BlockContainer from '@/components/BlockContainer';
import List from '@/components/UI/list/List';
import ListHeader from '@/components/UI/list/ListHeader';
import ListItem from '@/components/UI/list/ListItem';
import addValidationRules from '@/mixins/addValidationRules.js';
import canLandlordChargeVAT from '@/mixins/tenancy/canLandlordChargeVAT';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import Datepicker from '@modules/husleie-framework/src/components/forms/inputs/Datepicker.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Phone from '@modules/husleie-framework/src/components/forms/inputs/phone/Phone.vue';
import RadioButtonTapWithIcon from '@modules/husleie-framework/src/components/forms/inputs/radio-buttons/RadioButtonTapWithIcon.vue';
import SearchCompanies from '@modules/husleie-framework/src/components/forms/inputs/search/SearchCompanies.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import Switch from '@modules/husleie-framework/src/components/forms/inputs/switch/Switch.vue';
import SwitchWithLabels from '@modules/husleie-framework/src/components/forms/inputs/switch/SwitchWithLabels.vue';
import ErrorMessage from '@modules/husleie-framework/src/components/text/ErrorMessage.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import StringOperations from '@modules/husleie-framework/src/static-classes/StringOperations.class';
import { LandlordApi, TenancyApi } from '@modules/husleieapi2';
import * as moment from 'moment';
import { mask } from 'vue-the-mask';
import { mapGetters, mapActions } from 'vuex';
import { createCPIAdjustmentDateOptions } from '../../lib/CPIHelpers';
import InfoCard from '@/components/InfoCard.vue';
import axios from 'axios';

export default {
  components: {
    TenancyDepositRegisterForms,
    BlockContainer,
    List,
    ListHeader,
    ListItem,
    InfoCard,
    'hl-Input': Input,
    'hl-Button': Button,
    'hl-Phone': Phone,
    'hl-Select': Select,
    'hl-SearchCompanies': SearchCompanies,
    'hl-Datepicker': Datepicker,
    'hl-RadioButtonTapWithIcon': RadioButtonTapWithIcon,
    'hl-SwitchWithLabels': SwitchWithLabels,
    'hl-Currency': Currency,
    'hl-Switch': Switch,
    'hl-ErrorMessage': ErrorMessage,
  },

  props: {
    rentalObjectId: [Number, String],
    landlordId: [Number, String],
    defaultHeader: String,
  },
  mixins: [addValidationRules, canLandlordChargeVAT, i18nCommon],

  directives: { mask },

  data() {
    return {

      depDeposit: {
        category: "none",
        meta: {}, // Must be defined for the component to be rendered
      },
      depDepositFiles: [],
      depAccountOptions: [
        { name: 'Depositumsgaranti via Husleie.no', value: 'deposit_guarantee' },
        { name: 'Annen depositumsgaranti', value: 'other_deposit_guarantee' },
        { name: 'Depositumskonto i annen bank', value: 'other_deposit_account' },
        { name: 'Bankgaranti', value: 'bank_guarantee' },
        { name: 'NAV-garanti', value: 'nav_guarantee' },
        { name: 'Annen form for sikkerhet', value: 'other' },
      ],

      availableSubscriptions: [],
      durationType: 'limited',
      newBankAccountToggle: false,
      loading: false,
      landlordProfileBankAccounts: [],
      tenantIsCompany: false,

      tenancy: {
        tenant: {
          fullName: '',
          address: {
            postalZipcode: '',
            postalArea: '',
            street: '',
          },
          company: {
            organizationNumber: '',
            organizationName: '',
          },
        },
        coTenants: [],
        chosenPlan: null,
        contractDetails: {
          rent: {
            amount: null,
            vat: 0,
            invoicePeriod: 'month',
            dueDay: null,
          },
        },
        subProducts: [],
        bankInfo: { name: null, logo: null },
        bankAccount: {},
        CPIAdjustment: {},
        customerInfo: {},
      },

      subscriptionPlanInfo: {
        Free: {
          icon: 'budicon-savings',
          text: 'Ingen forvaltning',
        },
        Basic: {
          icon: 'budicon-mansion',
          text: 'Basic forvaltning',
        },
        Premium: {
          icon: 'budicon-business-area',
          text: 'Premium forvaltning',
        },
      },

      bindingstidOptions: [
        { name: 'Ingen bindingstid', value: '0' },
        { name: '1 måned', value: 1 },
        { name: '2 måneder', value: 2 },
        { name: '3 måneder', value: 3 },
        { name: '4 måneder', value: 4 },
        { name: '5 måneder', value: 5 },
        { name: '6 måneder', value: 6 },
        { name: '7 måneder', value: 7 },
        { name: '8 måneder', value: 8 },
        { name: '9 måneder', value: 9 },
        { name: '10 måneder', value: 10 },
        { name: '11 måneder', value: 11 },
        { name: '12 måneder', value: 12 },
      ],
      oppsigelsestidOptions: [
        { name: 'Ingen oppsigelsestid', value: 0 },
        { name: 'Egendefinert', value: 'egendefinert' },
        { name: '1 måned', value: 1 },
        { name: '2 måneder', value: 2 },
        { name: '3 måneder', value: 3 },
        { name: '4 måneder', value: 4 },
        { name: '5 måneder', value: 5 },
        { name: '6 måneder', value: 6 },
        { name: '7 måneder', value: 7 },
        { name: '8 måneder', value: 8 },
        { name: '9 måneder', value: 9 },
        { name: '10 måneder', value: 10 },
        { name: '11 måneder', value: 11 },
        { name: '12 måneder', value: 12 },
      ],

      dueDayOptions: [
        { name: '1', value: 1 },
        { name: '5', value: 5 },
        { name: '10', value: 10 },
        { name: '15', value: 15 },
        { name: '20', value: 20 },
        { name: '25', value: 25 },
      ],

      invoicePeriodOptions: [
        { name: 'måned', value: 'month' },
        { name: 'kvartal', value: 'quarter' },
      ],
    };
  },

  computed: {
    ...mapGetters(['getLandlordProfileMeta']),

    duePeriodOptions() {
      const { startDate } = this.tenancy.contractDetails;

      return [
        {
          name: StringOperations.capitalizeFirst(moment(startDate).subtract(1, 'month').format('MMMM')),
          value: 'advance',
        },
        {
          name: StringOperations.capitalizeFirst(moment(startDate).format('MMMM')),
          value: 'current',
        },
        {
          name: StringOperations.capitalizeFirst(moment(startDate).add(1, 'month').format('MMMM')),
          value: 'postpay',
        },
      ];
    },

    landlord() {
      return this.getLandlordProfileMeta(this.landlordId);
    },

    isLandlordProfessional() {
      return this.landlord?.type === 'professional';
    },

    isLandlordCompany() {
      return this.landlord?.organizationNumber;
    },

    bankAccountOptions() {
      if (this.landlordProfileBankAccounts == null) return;
      const options = [];
      this.landlordProfileBankAccounts.map(acc =>
        options.push({
          name: `${acc.alias ? `${acc.alias} -` : ''}  ${acc.bankAccount} - ${acc.bank.name}`,
          value: acc.bankAccount,
        })
      );
      return options;
    },

    CPIAdjustmentDateOptions() {
      const tenancyStartDate = this.tenancy.contractDetails.startDate;
      const dueDay = this.tenancy.contractDetails.rent.dueDay;

      return createCPIAdjustmentDateOptions(tenancyStartDate, dueDay, moment().format('YYYY-MM-DD')).map(value => ({
        name: moment(value, 'YYYY-MM-DD').format('YYYY MMMM'),
        value,
      }));
    },

    baseRent() {
      return this.tenancy.contractDetails.rent;
    },

    rentQuantity() {
      return this.tenancy.contractDetails.rent.invoicePeriod === 'month' ? 1 : 3;
    },

    netRent() {
      if (this.baseRent.amount == null) return null;
      return this.baseRent.amount * this.rentQuantity;
    },

    subProductsNetTotal() {
      if (this.tenancy.subProducts == null) return null;
      return this.tenancy.subProducts.reduce((acc, item) => acc + parseFloat(item.price || 0), 0);
    },

    subProductsTotalTax() {
      if (this.tenancy.subProducts == null) return null;
      return this.tenancy.subProducts.reduce((acc, item) => acc + (parseFloat(item.price || 0) * item.vat) / 100, 0);
    },

    netTotal() {
      if (this.netRent == null || this.subProductsNetTotal == null) return null;
      return this.netRent + this.subProductsNetTotal;
    },

    totalTax() {
      if (this.netRent == null || this.baseRent == null || this.subProductsTotalTax == null) return null;
      return (this.netRent * this.baseRent.vat) / 100 + this.subProductsTotalTax;
    },

    totalWithTax() {
      if (this.netTotal == null || this.totalTax == null) return null;
      return this.netTotal + this.totalTax;
    },

    newTenancyData() {
      const {
        rentalObjectId,
        tenant,
        coTenants,
        contractDetails,
        subProducts,
        bankAccount,
        CPIAdjustment,
        customerInfo,
        chosenPlan,
      } = JSON.parse(JSON.stringify(this.tenancy));

      const subscription = {
        subscriptionPlanId: chosenPlan,
      };

      tenant.type = 'tenant';

      if (this.tenantIsCompany) {
        tenant.organizationName = tenant.company.organizationName;
        tenant.organizationNumber = tenant.company.organizationNumber;

        tenant.dateOfBirth = undefined;
      } else if (tenant.dateOfBirth) {
        tenant.dateOfBirth = moment(tenant.dateOfBirth, 'DD.MM.YYYY').format('YYYY-MM-DD');
      } else {
        tenant.dateOfBirth = undefined
      }

      delete tenant.company;

      const details = {};

      if (contractDetails.noticePeriod) details.noticePeriod = parseInt(contractDetails.noticePeriod);

      if (contractDetails.bindingPeriod) details.bindingPeriod = parseInt(contractDetails.bindingPeriod);

      if (customerInfo.customerClientId) details.customerClientId = customerInfo.customerClientId;

      if (customerInfo.poNumber) details.poNumber = customerInfo.poNumber;

      for (let i = 0; i < subProducts.length; i++) {
        subProducts[i].price = parseInt(subProducts[i].price);
        subProducts[i].vat = parseInt(subProducts[i].vat);
      }

      let rentAdjustment;
      if (CPIAdjustment.CPIAdjustmentActive) {
        const lastAdjustment = moment(CPIAdjustment.nextCPIAdjustment, 'YYYY-MM-DD')
          .add(-1, 'year')
          .format('YYYY-MM-DD');
        rentAdjustment = {
          CPIAdjustment: CPIAdjustment.CPIAdjustmentActive,
          lastCPIAdjustment: lastAdjustment,
        };
      }

      const rent = {
        rent: parseInt(contractDetails.rent.amount),
        vat: parseInt(contractDetails.rent.vat),
        invoicePeriod: contractDetails.rent.invoicePeriod,
        dueDay: parseInt(contractDetails.rent.dueDay),
        duePeriod: contractDetails.rent.duePeriod,
        bankAccount: bankAccount.accountNumber,
      };

      const root_items = {
        rentalObjectId: parseInt(this.rentalObjectId),
        startDate: contractDetails.startDate,
        endDate: contractDetails.endDate,
      };

      return {
        rentalObjectId,
        rent,
        tenant,
        coTenants,
        subProducts,
        rentAdjustment,
        subscription,
        details,
        ...root_items,
      };
    },
    isPremiumSelected() {
      return this.availableSubscriptions
        .find(sub => sub.id === this.tenancy.chosenPlan)
        ?.name === 'Premium'
    },
    translationPeriodInfo() {
      const { dueDay } = this.tenancy.contractDetails.rent;
      let month;

      switch (this.tenancy.contractDetails.rent.duePeriod) {
        case 'advance':
          month = moment(this.tenancy.contractDetails.startDate).subtract('1', 'month').format('MMMM');
          break;
        case 'current':
          month = moment(this.tenancy.contractDetails.startDate).format('MMMM');
          break;
        case 'postpay':
          month = moment(this.tenancy.contractDetails.startDate).add('1', 'month').format('MMMM');
          break;
        default:
          return '';
      }
      const dateString = `${dueDay.padStart(2, 0)}. ${month}`;

      return this.translate('rentalObjectNewTenancyForm.the rent for {tenancyStartMonth} is to be paid by {date}', {
        tenancyStartMonth: moment(this.tenancy.contractDetails.startDate).format('MMMM'),
        date: dateString,
      });
    },
    translationDuePeriodLabel() {
      return this.translate('rentalObjectNewTenancyForm.duePeriod.label');
    },
    translationDuePeriodPlaceholder() {
      return this.translate('rentalObjectNewTenancyForm.duePeriod.placeholder');
    },
    translationErrorCouldNotRetrieveBankAccounts() {
      return this.translate('error.could not retrieve bank account');
    },
    translationErrorCouldNotCreateTenancy() {
      return this.translate('error.could not create tenancy');
    },
    translationErrorCouldNotGetBankInfo() {
      return this.translate('error.could not retrieve bank info');
    },
    translationErrorCouldNotGetSubscriptions() {
      return this.translate('error.could not retrieve available subscriptions');
    },
  },

  methods: {
    ...mapActions(['loadLandlordProfileBankAccounts']),
    ...mapActions('tenancy', ['getBankInformationByAccountPrefix', 'updateTenancyDeposit']),

    cancelTenancy() {
      this.$router.push({
        name: 'rentalObject_tenancies',
        params: {
          rentalObjectId: this.rentalObjectId,
        },
      });
    },

    addCoTenantItem() {
      this.tenancy.coTenants.push({
        fullName: '',
        cellPhone: '',
        email: '',
        type: 'cotenant',
      });
    },

    removeCoTenantItem(idx) {
      this.tenancy.coTenants.splice(idx, 1);
    },

    addSubProductItem() {
      this.tenancy.subProducts.push({
        name: '',
        vat: 0,
        price: 0,
      });
    },

    removeSubProductItem(idx) {
      this.tenancy.subProducts.splice(idx, 1);
    },

    async loadAccounts() {
      try {
        const res = await this.loadLandlordProfileBankAccounts();
        this.landlordProfileBankAccounts = res;
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotRetrieveBankAccounts, 'error');
      }
    },

    async loadAvailableSubscriptions() {
      const api = new LandlordApi();
      try {
        this.availableSubscriptions = await api.getLandlordAvailableSubscriptions(this.landlordId);
        this.availableSubscriptions.unshift({ name: "Free", price: 0, id: 0, });
      } catch (error) {
        this.$toast(this.translationErrorCouldNotGetSubscriptions, 'error');
      }
    },

    async getBankInfo(prefix) {
      try {
        this.tenancy.bankInfo = await this.getBankInformationByAccountPrefix(prefix);
      } catch (error) {
        this.$toast(this.translationErrorCouldNotGetBankInfo, 'error');
      }
    },

    fetchPostalArea(e) {
      const that = this;

      // Make a request for a user with a given ID
      if (e.match(/^\d\d\d\d$/)) {
        axios
          .get('/api/poststed/' + e)
          .then(function (response) {
            // handle success
            that.tenancy.tenant.address.postalArea = response.data.poststed;
          })
          .catch(function (error) {
            // handle error
            console.error(error);
          });
      }
    },

    populateCompany(c) {
      this.tenancy.tenant.company.organizationName = c.navn;
      this.tenancy.tenant.company.organizationNumber = c.organisasjonsnummer;
    },

    setDepDeposit(deposit) {
      this.depDeposit = deposit;
    },

    handleDepositFilesUpload(files) {
      this.depDepositFiles = files;
    },

    async onSubmit() {
      const success = await this.$refs.newTenancyForm.validate();

      if (!success) {
        this.$nextTick(() => {
          const el = document.querySelector('[class*=is-invalid]');
          if (el) {
            el.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        });
        return;
      }

      await this.registerTenancy();
      this.$refs.newTenancyForm.reset();
    },

    async registerTenancy() {
      this.loading = true;
      try {
        const api = new TenancyApi();
        if (this.newTenancyData.tenant.address.postalArea === '') {
          delete this.newTenancyData.tenant.address.postalArea;
        }
        if (this.newTenancyData.tenant.address.postalZipcode === '') {
          delete this.newTenancyData.tenant.address.postalZipcode;
        }

        if (this.newTenancyData.tenant.address.street === '') {
          delete this.newTenancyData.tenant.address.street;
        }
        // if no keys in address, delete the empty object
        if (Object.keys(this.newTenancyData.tenant.address).length === 0) {
          delete this.newTenancyData.tenant.address;
        }
        const result = await api.createLandlordTenancy(this.landlordId, { body: this.newTenancyData });

        // Save deposit if one was selected
        if (this.depDeposit.category !== 'none') {

          // Upload files if any. Only first file will be uploaded
          if (this.depDepositFiles.length > 0) {
            try {
              const formData = new FormData();
              formData.append('files[0]', this.depDepositFiles[0].file);

              const uploadRes = await axios.post('/upload/', formData, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
              });

              if (uploadRes.data.uploaded_files?.length) {
                this.depDeposit.fileToken = uploadRes.data.uploaded_files[0].token;
              }
            } catch (error) {
              console.error('Failed to upload deposit files:', error);
              this.$toast('Kunne ikke laste opp depositumsdokumentasjon.');
            }
          }

          // Generate UUID for new deposit
          const UUIDv4 = function b(a) {
            return a
              ? (a ^ ((Math.random() * 16) >> (a / 4))).toString(16)
              : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, b);
          };
          const depositId = UUIDv4();

          // Save deposit
          try {
              await this.updateTenancyDeposit({
              landlordId: this.landlordId,
              tenancyId: result.id,
              depositId: depositId,
              data: this.depDeposit,
            });
          } catch (error) {
            console.error('Failed to save deposit:', error);
            this.$toast('Leieforholdet ble opprettet, men deposituminformasjon kunne ikke lagres. Vennligst legg det til manuelt.', 'warning');
          }
        }

        this.$router.push({ name: 'tenancy_overview', params: { landlordId: this.landlordId, tenancyId: result.id } });
      } catch (error) {
        this.$toast(this.translationErrorCouldNotCreateTenancy, error.toString());
      }
      this.loading = false;
    },
  },

  watch: {
    'tenancy.bankAccount.accountNumber'(newVal) {
      if (newVal.length == 4) {
        this.getBankInfo(newVal);
      }
    },
    'tenancy.CPIAdjustment.CPIAdjustmentActive'(newVal) {
      if (newVal == false) {
        this.tenancy.CPIAdjustment.nextCPIAdjustment = null;
        this.$refs.nextCPIAdj.reset();
      }
    },
    durationType(v) {
      if (v === 'unlimited') {
        this.tenancy.contractDetails.endDate = null;
      }
    },
    'tenancy.contractDetails.rent.invoicePeriod'(newVal) {
      if (newVal === 'quarter') {
        this.tenancy.contractDetails.rent.duePeriod = 'current';
      }
    },
  },

  mounted() {
    this.loadAccounts();
    this.loadAvailableSubscriptions();
  },
};
</script>

<style scoped lang="scss">
.blockContainer {
  padding: 1.5rem;
}

@media (min-width: 768px) {
  .blockContainer {
    padding: 3rem;
  }
}

.newTenancyFormGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem 2rem;
}

.newTenancyFormGrid4 {
  display: grid;
  grid-template-columns: 1fr 0.7fr 1fr 0.5fr;
  padding: 0rem 1rem;
  align-items: center;
  gap: 0rem 1rem;
}

.buttons {
  display: grid;
  grid-template-columns: 1;
  gap: 1rem 0rem;
  margin-top: 4rem;
}

@media (min-width: 945px) {
  .buttons {
    display: grid;
    grid-template-columns: repeat(2, 200px);
    justify-content: space-between;
    gap: 0rem 2rem;
    margin-top: 6rem;
  }
  .newTenancyFormGrid4 {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    padding: 0rem 1rem;
    align-items: center;
    gap: 0rem 1rem;
    width: 100%;
  }
}

.add-row {
  background: $gray-100;
  color: $gray-800;
  box-shadow: $box-shadow-1;
  cursor: pointer;
  margin-bottom: 0 !important;
}

.coTenant-phone {
  flex: 0 0 180px;
}

.delete-list-item {
  flex: 0 0 80px;
  text-align: right;
}

.bank-account-img {
  border-radius: 50%;
  max-width: 120px;
  border: 1px solid $gray-200;
  box-shadow: $box-shadow-1;
}
</style>
